





































































































































































import Vue from 'vue'
import fb from '@/firebase'
import ClipLoader from '@/components/vue-spinner/ClipLoader.vue'
import LibratoChart, { LibratoChartIds } from '@/components/LibratoChart'
import SelfTestSummary from '@/components/SelfTestSummary'
import { UserProfile, UserMediaDevice } from '@/types/users'
import { UserNetworkStats } from '@/types/networkstats'
import { UnsubscribeFunction } from '@/store/types'
import './CasterDetails.scss'
import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { Dropdown, DropdownItem, DropdownMenu, Option, Select } from 'element-ui'
import { isEqual } from 'lodash'
import moment from 'moment'
import { Cast } from '@/types/casts'
import helpers from '@/helpers'

export default Vue.extend({
  name: 'CasterDetails',
  props: {
    caster: {
      type: String,
      required: true
    },
    errors: {
      type: Array,
      required: true
    },
    k360eventId: {
      type: Number,
      required: false
    },
    feedNames: {
      type: Array as () => string[],
      required: false,
    },
    castInfo: {
      type: Object as () => Partial<Cast>,
      required: false,
    }
  },
  data: () => ({
    profile: null as null|UserProfile,
    stats: null as null|UserNetworkStats,
    unsubscribeProfile: null as null|UnsubscribeFunction,
    unsubscribeStats: null as null|UnsubscribeFunction,
    chartDuration: 1800, // seconds
    chartsVisible: true,
    selectedFeedName: '',
  }),
  methods: {
    getDeviceStyle (device, selected) {
      if (device.id === selected) {
        return {
          color: '#70BC24'
        }
      }
      return {
        color: '#AAA'
      }
    },
    setChartDuration (duration: number) {
      this.chartDuration = duration
      this.forceChartUpdate()
    },
    casterMetricsurl () {
      return this._metricsurl(encodeURI(this.chartQueryCaster))
    },
    openMetricsUrl (feedName) {
      window.open(this.metricsurl(feedName), '_blank')
    },
    metricsurl (feedName) {
      this.selectedFeedName = feedName
      return this._metricsurl(encodeURI(this.chartQuery))
    },
    _metricsurl (chartQuery) {
      const libratoSpace = 62842
      const start_time = this.castInfo.start_date.seconds
      const end_time = this.castInfo.end_date.seconds
      const duration = end_time - start_time
      return `https://metrics.librato.com/s/spaces/${libratoSpace}?duration=${duration}&end_time=${end_time}&source=${chartQuery}`
    },
    // setChartFeed (feedName) {
    //   this.selectedFeedName = feedName
    //   this.forceChartUpdate()
    // },
    forceChartUpdate () {
      // force redraw of Librato Charts (retrigger script)
      this.chartsVisible = false
      setTimeout(() => {
        this.chartsVisible = true
      }, 10)
    }
  },
  computed: {
    chartIdJitterAndLatency (): LibratoChartIds {
      return LibratoChartIds.CLIENT_LATENCY_JITTER
    },
    chartIdMixerBitrates (): LibratoChartIds {
      return LibratoChartIds.MIXER_BITRATES
    },
    chartIdMixerBuffers (): LibratoChartIds {
      return LibratoChartIds.MIXER_BUFFERS
    },
    chartIdClockOffsets (): LibratoChartIds {
      return LibratoChartIds.INGEST_CLOCK_OFFSET
    },
    castIsActive (): boolean {
      return !this.castInfo || helpers.isActiveCast(this.castInfo)
    },
    durationLabel (): string {
      return moment
        .duration(this.chartDuration, "seconds")
        .humanize()
        .replace('an', '')
    },
    canEditCast (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.Cast)
    },
    canDoOps (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    chartQueryCaster (): string {
      // query used by librato charts
      return `*${this.k360eventId}.${this.caster}`
    },
    chartQuery (): string {
      // query used by librato charts
      return `*${this.k360eventId}.${this.selectedFeedName}`
    },
    cameras (): UserMediaDevice[] {
      if (this.profile === null || this.profile.devices === undefined) {
        return []
      }
      return this.profile.devices.filter(device => device.type === 'video')
    },
    microphones (): UserMediaDevice[] {
      if (this.profile === null || this.profile.devices === undefined) {
        return []
      }
      return this.profile.devices.filter(device => device.type === 'audio')
    },
    outputs (): UserMediaDevice[] {
      if (this.profile === null || this.profile.devices === undefined) {
        return []
      }
      return this.profile.devices.filter(device => device.type === 'output')
    },
    adaptationText (): string {
      if (this.stats === null) {
        return ''
      }
      if (this.stats.encoder?.adaptation === 'bandwidth') {
        return '(downscaled to fit in available bandwidth)'
      }
      if (this.stats.encoder?.adaptation === 'cpu') {
        return '(downscaled because of high cpu usage)'
      }
      return ''
    },
    videoBitrate (): number {
      if (this.stats === null || this.stats.encoder === undefined) {
        return 0
      }
      return Math.floor(this.stats.encoder.video_bitrate / 102.4) / 10
    },
    audioBitrate (): number {
      if (this.stats === null || this.stats.encoder === undefined) {
        return 0
      }
      return Math.floor(this.stats.encoder.audio_bitrate / 102.4) / 10
    },
    totalBitrate (): number {
      if (this.stats === null || this.stats.encoder === undefined) {
        return 0
      }
      return Math.floor((this.stats.encoder.audio_bitrate + this.stats.encoder.video_bitrate) / 102.4) / 10
    },
    uploadSpeed (): string {
      if (!this.stats || !this.stats.upload || this.stats.upload.bandwidth === undefined) {
        return '0 Mbps'
      }
      if (this.stats.upload.bandwidth < 15) {
        return (Math.round(this.stats.upload.bandwidth * 10) / 10) + 'Mbps'
      }
      return '> 15Mbps'
    },
    downloadSpeed (): string {
      if (!this.stats || !this.stats.download || this.stats.download.bandwidth === undefined) {
        return '0 Mbps'
      }
      if (this.stats.download.bandwidth < 15) {
        return (Math.round(this.stats.download.bandwidth * 10) / 10) + 'Mbps'
      }
      return '> 15Mbps'
    },
    logsurl (): string {
      if (this.profile === null) {
        return ''
      }

      if (this.castIsActive) {
        return `https://app.logz.io/#/dashboard/kibana/discover?_a=(columns:!(message),index:'logzioCustomerIndex*',interval:auto,query:(language:lucene,query:'cc_uid:${this.profile.id}'),sort:!('@timestamp',desc))&_g=()&accountIds=14578`
      }

      // archived casts
      const startDate = moment(this.castInfo.start_date.seconds * 1000).toISOString()
      const endDate = moment(this.castInfo.end_date.seconds * 1000).toISOString()
      let query = `https://app.logz.io/#/dashboard/kibana/discover?_a=(columns:!(message),index:'logzioCustomerIndex*',interval:auto,query:(language:lucene,query:'cc_uid:${this.profile.id}'),sort:!('@timestamp',desc))&_g=(refreshInterval:(pause:!t,value:0),`
      query += `time:(from:'${startDate}',to:'${endDate}'))&accountIds=14578&switchToAccountId=14578`
      return query
    },
    logsUIurl (): string {
      if (this.profile === null) {
        return ''
      }

      if (this.castIsActive) {
        return `https://app.logz.io/#/dashboard/kibana/discover?_a=(columns:!(message,cc_UIevent),index:'logzioCustomerIndex*',interval:auto,query:(language:lucene,query:'cc_uid:${this.profile.id}%20AND%20cc_UIevent:ui.*'),sort:!(\'@timestamp\',desc))&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-15m,to:now))&accountIds=14578&switchToAccountId=14578`
      }

      // archived casts
      const startDate = moment(this.castInfo.start_date.seconds * 1000).toISOString()
      const endDate = moment(this.castInfo.end_date.seconds * 1000).toISOString()
      let query = `https://app.logz.io/#/dashboard/kibana/discover?_a=(columns:!(message,cc_UIevent),index:'logzioCustomerIndex*',interval:auto,query:(language:lucene,query:'cc_uid:${this.profile.id}%20AND%20cc_UIevent:ui.*'),sort:!('@timestamp',desc))&_g=(refreshInterval:(pause:!t,value:0),`
      query += `time:(from:'${startDate}',to:'${endDate}'))&accountIds=14578&switchToAccountId=14578`
      return query
    },
    sentryurl (): string {
      if (this.profile === null) {
        return ''
      }

      if (this.castIsActive) {
        return `https://sentry.io/organizations/kiswe/issues/?query=user.id%3A${this.profile.id}&statsPeriod=14d`
      }

      // archived casts
      const startDate = moment(this.castInfo.start_date.seconds * 1000).toISOString()
      const endDate = moment(this.castInfo.end_date.seconds * 1000).toISOString()
      let query = `https://sentry.io/organizations/kiswe/issues/?query=user.id%3A${this.profile.id}%2Fis%3Aunresolved%20url%3Ahttps%3A%2F%2Fstudio.kiswe.com`
      query += `&start=${startDate}&end=${endDate}&utc=true`
      return query
    }
  },
  watch: {
    feedNames (newArray, oldArray) {
      if (isEqual(newArray, oldArray)) {
        return
      }
      // default = last one
      this.selectedFeedName = (newArray && newArray.length) ? newArray[newArray.length - 1] : ''
    }
  },
  mounted () {
    // default = last one
    this.selectedFeedName = (this.feedNames && this.feedNames.length) ? this.feedNames[this.feedNames.length - 1] : ''

    this.unsubscribeProfile = fb.db.collection('profiles').doc(this.caster).onSnapshot(snap => {
      const profile = snap.data() as UserProfile | undefined
      if (profile) {
        this.profile = profile
      }
    }, error => {
      console.error('error unsubscribeProfile ' + this.caster + ' ' + error)
    })
    this.unsubscribeStats = fb.db.collection('networkstats').doc(this.caster).onSnapshot(snap => {
      const stats = snap.data() as undefined | UserNetworkStats
      if (stats) {
        this.stats = stats
      }
    }, error => {
      console.error('error unsubscribeStats ' + this.caster + ' ' + error)
    })
  },
  destroyed () {
    if (this.unsubscribeProfile) {
      this.unsubscribeProfile()
    }
    if (this.unsubscribeStats) {
      this.unsubscribeStats()
    }
  },
  components: {
    'clip-loader': ClipLoader,
    SelfTestSummary,
    LibratoChart,
    'el-select': Select,
    'el-option': Option,
    'el-dropdown': Dropdown,
    'el-dropdown-menu': DropdownMenu,
    'el-dropdown-item': DropdownItem,
  }
})
